import DataLoader from 'dataloader'
import { collection } from '@/infra/collection'
import {
  SubmittedJob,
  type SubmittedJobProps,
} from '@/features/jobs/details/SubmittedJob'
import { JobLegend } from '@/features/jobs/job-legend'
import type { JobDataLoader } from '@/features/jobs/JobDataLoader'
import {
  type Job as JobProto,
  type JobUpdatedRealtimeEvent as JobUpdatedRealtimeEventProto,
} from '@taxfyle/api-internal/internal/job_pb'
import {
  mapJobProtoStatusToStatus,
  mapJobProtoToJobType,
  mapJobProtoToTitle,
  mapJobScopeItemsProtoToJobScopeItems,
  mapLegendToProps,
  mapMemberProtoToCollaboratorProps,
} from '@/features/jobs/job-mappers'
import type { PrimeUserFn, PrimeWorkspaceFn } from '@/features/iam'
import { Id } from '@/infra/id'
import type { JobsApi } from '@/features/jobs/api/job-api'
import { map } from 'rxjs'
import { filterNonNil } from '@/utils/rx-util'
import { safeDecimalToNumber, safeTimestampToDate } from '@/utils/grpc-util'
import type { FetchJobOptions } from '@/features/jobs'
import { mapJobScopeChangeStatusProtoToStatus } from '@/features/jobs/job-scope-changes/job-scope-changes-mappers'

/**
 * Store that manages detailed, non-draft jobs.
 */
export class SubmittedJobStore {
  private readonly _loader = new DataLoader(this._fetchManyCore.bind(this))

  readonly jobs = collection({
    create: () => new SubmittedJob(),
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
  })

  constructor(
    private readonly _api: JobsApi,
    private readonly _jobDataLoader: JobDataLoader,
    private readonly _primeUser: PrimeUserFn,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
  ) {
    this.fetchJob = this.fetchJob.bind(this)

    // When the inner job loader clears an entry, clear it in our loader
    // as well.
    this._jobDataLoader.clear$.subscribe((id) => this._loader.clear(id))

    this._api.realtime$
      .pipe(
        map((e) => e.getJobUpdated()),
        filterNonNil(),
      )
      .subscribe(this._onRemoteEvent.bind(this))
  }

  /**
   * Fetches a job by ID.
   *
   * @param id
   * @param options
   */
  async fetchJob(
    id: Id,
    options?: FetchJobOptions,
  ): Promise<SubmittedJob | null> {
    if (!Id.some(id)) {
      return null
    }

    if (options?.force) {
      this._jobDataLoader.clear(id)
    }

    return this._loader.load(Id.assert(id))
  }

  async _fetchManyCore(ids: readonly string[]) {
    const response = await this._jobDataLoader.fetchMany(ids)
    const props = response.map((r) => {
      if (!r) {
        return null
      }

      if (mapJobProtoStatusToStatus(r.getStatus()) === 'UnderConstruction') {
        // Drafts are not submitted.
        return null
      }

      return this._mapJobProtoToProps(r)
    })
    return this.jobs.set(props)
  }

  private _onRemoteEvent(event: JobUpdatedRealtimeEventProto) {
    // Ignore drafts.
    const proto = event.getJob()!

    if (mapJobProtoStatusToStatus(proto.getStatus()) === 'UnderConstruction') {
      return
    }

    const props = this._mapJobProtoToProps(proto)
    this.jobs.set(props)
  }

  private _mapJobProtoToProps(proto: JobProto): SubmittedJobProps {
    const legend = proto.getLegend()
    return {
      id: proto.getId(),
      workspace: this._primeWorkspace({ id: proto.getWorkspaceId() }),
      shortId: proto.getShortId(),
      conversationId: proto.getConversationId()?.getValue() ?? Id.none,
      title: mapJobProtoToTitle(proto),
      legendId: proto.getLegend()?.getId(),
      legendName: proto.getLegend()?.getName() ?? '(Unknown Legend)',
      status: mapJobProtoStatusToStatus(proto.getStatus()),
      type: mapJobProtoToJobType(this._primeUser, proto),
      submittedAt: safeTimestampToDate(proto.getDateTransmitted()),
      closedAt: safeTimestampToDate(proto.getDateClosed()),
      answers: proto.getAnswersList().map((a) => ({
        id: a.getId(),
        valueJson: a.getValue(),
      })),
      collaborators: proto
        .getMembersList()
        .map((m) => mapMemberProtoToCollaboratorProps(this._primeUser, m)),
      jobScopeItems: proto
        .getJobScopeItemsList()
        .map(mapJobScopeItemsProtoToJobScopeItems),
      total: safeDecimalToNumber(proto.getTotal()),
      primaryAmount: safeDecimalToNumber(proto.getPrimaryAmount()),
      couponAmount: safeDecimalToNumber(proto.getCouponAmount()),
      amendedAmount: safeDecimalToNumber(proto.getAmendedAmount()),
      couponCode: proto.getCouponCodesList()?.[0] ?? '',
      legend: legend ? mapLegendToProps(legend) : JobLegend.none,
      jobScopeChangeStatus: mapJobScopeChangeStatusProtoToStatus(
        proto.getScopeChangeStatus(),
      ),
    }
  }
}
